import { useState } from 'react';

import { CapabilityGate } from '../../../components/CapabilityGate';
import {
  useTenantLifecycleDetail,
  useTenantLifecycleMutations,
} from '../../../hooks/use-tenant-lifecycle-detail';

import { ChurningInitiationWizard } from './ChurningInitiationWizard';
import { TenantLifecycleHistoryTimeline } from './TenantLifecycleHistoryTimeline';
import { TenantStatusBadge } from './TenantStatusBadge';
import { TERMINAL_STATUSES } from './types';

import type { TenantStatus } from './types';

interface TenantLifecycleDetailDrawerProps {
  tenantId: string | null;
  onClose: () => void;
}

/**
 * TenantLifecycleDetailDrawer — drawer lateral com snapshot + history + ações.
 *
 * Layout:
 * - Header: nome + status badge + version
 * - Ações disponíveis (gated por capability):
 *   - Suspender (tenant-lifecycle-transition)
 *   - Reativar  (tenant-lifecycle-transition)
 *   - Iniciar churning (tenant-lifecycle-churning-initiate) → abre ChurningInitiationWizard
 *   - Cancelar churning (tenant-lifecycle-churning-initiate)
 * - Histórico de transições (tenant-lifecycle-read)
 *
 * D-B defense-in-depth: cada botão de ação tem próprio CapabilityGate.
 * Estados terminais: sem ações disponíveis (readonly).
 */
export function TenantLifecycleDetailDrawer({
  tenantId,
  onClose,
}: TenantLifecycleDetailDrawerProps): JSX.Element {
  const { data, isLoading, isError } = useTenantLifecycleDetail(tenantId);
  const [showChurningWizard, setShowChurningWizard] = useState(false);
  const [transitionError, setTransitionError] = useState<string | null>(null);

  const mutations = useTenantLifecycleMutations(tenantId ?? '');

  if (!tenantId) return <></>;

  const isTerminal = data?.tenant.status
    ? TERMINAL_STATUSES.includes(data.tenant.status as TenantStatus)
    : false;
  const isChurning = data?.tenant.status === 'churning';

  const handleSuspend = async () => {
    if (!data?.tenant) return;
    setTransitionError(null);
    try {
      await mutations.transition(
        { toStatus: 'suspended', reason: 'Suspensão manual' },
        data.tenant.version,
      );
    } catch (e) {
      setTransitionError(e instanceof Error ? e.message : 'Erro ao suspender tenant.');
    }
  };

  const handleReactivate = async () => {
    if (!data?.tenant) return;
    setTransitionError(null);
    try {
      await mutations.transition(
        { toStatus: 'active', reason: 'Reativação manual' },
        data.tenant.version,
      );
    } catch (e) {
      setTransitionError(e instanceof Error ? e.message : 'Erro ao reativar tenant.');
    }
  };

  const handleCancelChurning = async () => {
    if (!data?.tenant) return;
    setTransitionError(null);
    try {
      await mutations.cancelChurning(
        { reason: 'Cancelamento manual pelo operador' },
        data.tenant.version,
      );
    } catch (e) {
      setTransitionError(e instanceof Error ? e.message : 'Erro ao cancelar churning.');
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.3)',
          zIndex: 100,
        }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside
        role="complementary"
        aria-label="Detalhes de lifecycle do tenant"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 480,
          backgroundColor: '#fff',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
          zIndex: 101,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        }}
      >
        {/* Drawer header */}
        <div
          style={{
            padding: '20px 24px 16px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, marginBottom: 2 }}>
              TENANT LIFECYCLE · ADR-111
            </div>
            {data?.tenant && (
              <>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: '0 0 6px' }}>
                  {data.tenant.name}
                </h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <TenantStatusBadge status={data.tenant.status as TenantStatus} />
                  <span style={{ fontSize: 11, color: '#9ca3af' }}>
                    v{data.tenant.version} · {data.tenant.packageType}
                  </span>
                </div>
              </>
            )}
            {isLoading && (
              <div
                style={{ height: 20, width: 200, backgroundColor: '#e5e7eb', borderRadius: 4 }}
              />
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            style={{
              background: 'none',
              border: 'none',
              fontSize: 20,
              cursor: 'pointer',
              color: '#9ca3af',
              padding: 4,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, padding: '20px 24px', overflowY: 'auto' }}>
          {isError && (
            <div
              style={{
                padding: 16,
                backgroundColor: '#fef2f2',
                borderRadius: 8,
                color: '#991b1b',
                fontSize: 13,
              }}
            >
              Erro ao carregar detalhes do tenant.
            </div>
          )}

          {isLoading && (
            <div aria-busy="true" aria-label="Carregando...">
              {[60, 90, 70].map((w, i) => (
                <div
                  key={i}
                  style={{
                    height: 14,
                    width: `${w}%`,
                    backgroundColor: '#e5e7eb',
                    borderRadius: 4,
                    marginBottom: 8,
                  }}
                />
              ))}
            </div>
          )}

          {data?.tenant && (
            <>
              {/* Datas relevantes */}
              <section style={{ marginBottom: 20 }}>
                <h3
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#6b7280',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    margin: '0 0 10px',
                  }}
                >
                  Cronologia
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {data.tenant.activatedAt && (
                    <DateRow label="Ativado em" value={data.tenant.activatedAt} />
                  )}
                  {data.tenant.suspendedAt && (
                    <DateRow label="Suspenso em" value={data.tenant.suspendedAt} />
                  )}
                  {data.tenant.churningStartedAt && (
                    <DateRow label="Churning iniciado" value={data.tenant.churningStartedAt} />
                  )}
                  {data.tenant.churningCancellableUntil && (
                    <DateRow
                      label="Cancelável até"
                      value={data.tenant.churningCancellableUntil}
                      highlight={new Date(data.tenant.churningCancellableUntil) > new Date()}
                    />
                  )}
                  {data.tenant.churnedAt && (
                    <DateRow label="Churned em" value={data.tenant.churnedAt} />
                  )}
                  {data.tenant.archivedAt && (
                    <DateRow label="Arquivado em" value={data.tenant.archivedAt} />
                  )}
                </div>
              </section>

              {/* Erro de transição */}
              {transitionError && (
                <div
                  style={{
                    padding: '10px 12px',
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: 8,
                    fontSize: 12,
                    color: '#991b1b',
                    marginBottom: 16,
                  }}
                >
                  {transitionError}
                </div>
              )}

              {/* Ações — D-B: cada botão com próprio capability gate */}
              {!isTerminal && (
                <section style={{ marginBottom: 20 }}>
                  <h3
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#6b7280',
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      margin: '0 0 10px',
                    }}
                  >
                    Ações
                  </h3>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {/* Suspender — apenas se active/payment_overdue */}
                    {(data.tenant.status === 'active' ||
                      data.tenant.status === 'payment_overdue') && (
                      <CapabilityGate capability="tenant-lifecycle-transition">
                        <button
                          type="button"
                          onClick={() => {
                            void handleSuspend();
                          }}
                          disabled={mutations.isTransitioning}
                          style={{
                            padding: '10px 14px',
                            borderRadius: 8,
                            border: '1px solid #fbbf24',
                            backgroundColor: '#fffbeb',
                            color: '#92400e',
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: 'pointer',
                            textAlign: 'left',
                          }}
                        >
                          Suspender Tenant
                        </button>
                      </CapabilityGate>
                    )}

                    {/* Reativar — apenas se suspended */}
                    {data.tenant.status === 'suspended' && (
                      <CapabilityGate capability="tenant-lifecycle-transition">
                        <button
                          type="button"
                          onClick={() => {
                            void handleReactivate();
                          }}
                          disabled={mutations.isTransitioning}
                          style={{
                            padding: '10px 14px',
                            borderRadius: 8,
                            border: '1px solid #6ee7b7',
                            backgroundColor: '#f0fdf4',
                            color: '#065f46',
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: 'pointer',
                            textAlign: 'left',
                          }}
                        >
                          Reativar Tenant
                        </button>
                      </CapabilityGate>
                    )}

                    {/* Iniciar churning — não disponível se já churning */}
                    {!isChurning && (
                      <CapabilityGate capability="tenant-lifecycle-churning-initiate">
                        <button
                          type="button"
                          onClick={() => {
                            setShowChurningWizard(true);
                          }}
                          style={{
                            padding: '10px 14px',
                            borderRadius: 8,
                            border: '1px solid #fca5a5',
                            backgroundColor: '#fef2f2',
                            color: '#991b1b',
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: 'pointer',
                            textAlign: 'left',
                          }}
                        >
                          Iniciar processo de churning...
                        </button>
                      </CapabilityGate>
                    )}

                    {/* Cancelar churning — apenas se churning e dentro da janela */}
                    {isChurning &&
                      data.tenant.churningCancellableUntil &&
                      new Date(data.tenant.churningCancellableUntil) > new Date() && (
                        <CapabilityGate capability="tenant-lifecycle-churning-initiate">
                          <button
                            type="button"
                            onClick={() => {
                              void handleCancelChurning();
                            }}
                            disabled={mutations.isCancellingChurning}
                            style={{
                              padding: '10px 14px',
                              borderRadius: 8,
                              border: '1px solid #a5b4fc',
                              backgroundColor: '#eff6ff',
                              color: '#3730a3',
                              fontSize: 13,
                              fontWeight: 600,
                              cursor: 'pointer',
                              textAlign: 'left',
                            }}
                          >
                            {mutations.isCancellingChurning
                              ? 'Cancelando...'
                              : 'Cancelar churning (dentro da janela reversível)'}
                          </button>
                        </CapabilityGate>
                      )}
                  </div>
                </section>
              )}

              {isTerminal && (
                <div
                  style={{
                    padding: '10px 12px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: 8,
                    fontSize: 12,
                    color: '#6b7280',
                    marginBottom: 20,
                  }}
                >
                  Estado terminal — sem ações disponíveis. Operação irreversível (ADR-111).
                </div>
              )}

              {/* Histórico */}
              <section>
                <h3
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#6b7280',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    margin: '0 0 12px',
                  }}
                >
                  Histórico de transições
                </h3>
                <TenantLifecycleHistoryTimeline history={data.history} />
              </section>
            </>
          )}
        </div>
      </aside>

      {/* Churning Wizard Modal */}
      {showChurningWizard && data?.tenant && (
        <ChurningInitiationWizard
          tenant={data.tenant}
          onConfirm={async (body) => {
            await mutations.initiateChurning(body, data.tenant.version);
          }}
          onClose={() => {
            setShowChurningWizard(false);
          }}
          isLoading={mutations.isInitiatingChurning}
        />
      )}
    </>
  );
}

// ─── sub-components ───────────────────────────────────────────────────────

function DateRow({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}): JSX.Element {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
      <span style={{ color: '#6b7280' }}>{label}</span>
      <span style={{ color: highlight ? '#059669' : '#374151', fontWeight: highlight ? 600 : 400 }}>
        {new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
      </span>
    </div>
  );
}
